// src/tabs/CalendarPanel.jsx — Phase 2.G.6 (ADR-033) + PR #169d (vue Aujourd'hui)
//   + PR #169c (vue Liste)
// Container : charge les résas du mois, gère viewMode + filtres + recherche
// + filtre statut métier, render le bon View enfant.
//
// 3 vues disponibles (toggle persisté dans localStorage 'fbz-calendar-view') :
//   - 📊 Tableau (DEFAULT) : Gantt-style, 1 ligne logement × 1 col jour
//   - 🌅 Aujourd'hui : focus mouvements du jour (rotations + solo départs/arrivées)
//   - 📋 Liste (PR #169c) : tableau filtrable + triable résas ∪ blocks direct interne
//
// Note : MensuelView retirée PR #169d (redondante avec TableauView).
// safeViewMode() route 'mensuel' legacy → 'tableau' (fallback gracieux).

import { useState, useEffect, useMemo, useCallback } from "react"
import {
  ChevronLeft, ChevronRight, Search, Ruler, Lock, AlertTriangle,
  Table2, Sunrise, List,
} from "lucide-react"
import { supabase } from "../supabase"
import { DS } from "../lib/ui"
import ResaV2Modal from "../modals/ResaV2Modal"
// Phase 6.B PR #167a (ADR-099) — modal unifiée Option Hybride remplace
// PricingManualEditModal + PricingBlockCreateModal côté CalendarPanel.
// Les thin wrappers `PricingManualEditModal` + `PricingBlockCreateModal`
// existent toujours pour rétro-compat (callers externes hypothétiques).
import UnifiedDateActionModal from "../modals/UnifiedDateActionModal"
// PR #169a-bis (ADR-105) — PricingBlockDetailsModal SUPPRIMÉ.
// Les blocks ouvrent désormais ResaV2Modal itemKind='pricing_block'.
import { useDailyPricing } from "../lib/use-daily-pricing"
import { MONTHS } from "../lib/format"
import { getAllLogsFlat } from "../lib/sousloc-helpers"
import { countActiveResas } from "../lib/calendar-active-resas"
import TableauView from "./calendar/TableauView"
import { buildOutboxPendingByLogement, countOutboxPending } from "./calendar/_outbox-helpers"
import TodayView from "./calendar/TodayView"
import ListeView from "./calendar/ListeView"
import CalendarLegend, { SHOW_CANCELLED_LS_KEY, parseShowCancelled } from "./calendar/CalendarLegend"
import Beds24DivergenceModal from "./calendar/Beds24DivergenceModal"
import { pruneReopenedSegments } from "./calendar/_beds24-origin" // MINEUR-1 revue #993
// Lot AUTO-REFRESH — cache mémoire stale-while-revalidate (retour d'onglet
// instantané, audit §3/QW6) + realtime « signal → refetch » (audit §d).
import { swrGet, swrSet } from "../lib/swr-cache"
import { useRealtimeRefresh } from "../lib/use-realtime-refresh"

const VIEW_LS_KEY = 'fbz-calendar-view'
// PR #167b (ADR-100) — toggle "📐 Min nuits" persistant.
const SHOW_MIN_STAY_LS_KEY = 'fbz-calendar-show-min-stay'
// PR #169d — vue Mensuel retirée (redondante avec Tableau).
// PR #169c — 3e vue 'liste' ajoutée (tableau filtrable + triable).
// DA — libellés sans emoji + icône lucide par vue (rendu dans la bascule).
const VIEWS = [
  { id: 'tableau', label: 'Tableau', Icon: Table2, enabled: true },
  { id: 'aujourdhui', label: "Aujourd'hui", Icon: Sunrise, enabled: true },
  { id: 'liste', label: 'Liste', Icon: List, enabled: true },
]

// PR #169d — F-9 fallback strict allowlist legacy 'mensuel' → 'tableau'.
// PR #169c — étend allowlist à 'liste'.
// Toute valeur non listée (LS corrompu, valeur exotique) → 'tableau' par défaut.
function safeViewMode(raw) {
  return ['tableau', 'aujourdhui', 'liste'].includes(raw) ? raw : 'tableau'
}

// Filtre rapide statut métier (Phase 2.G.6 #11)
// DA — libellés sans emoji (les <option> HTML ne peuvent pas rendre d'icône).
const STATUT_FILTERS = [
  { id: 'all',                 label: 'Toutes les résas' },
  { id: 'a-relancer-paiement', label: 'À relancer paiement (Booking/Direct, J-3 ou moins, non payé)' },
  { id: 'caution-ko',          label: 'Caution non reçue (Booking/Direct, payé)' },
  { id: 'arrivee-today',       label: 'Arrivée aujourd’hui' },
  { id: 'depart-today',        label: 'Départ aujourd’hui' },
  { id: 'sur-place',           label: 'Sur place actuellement' },
  { id: 'a-venir-7j',          label: 'À venir cette semaine (J+1 → J+7)' },
]

function dateOnlyFromISO(s) {
  if (!s) return null
  const d = new Date(s); d.setHours(0, 0, 0, 0); return d
}

function applyStatutFilter(resas, statut, today) {
  if (statut === 'all') return resas
  return resas.filter(r => {
    const start = dateOnlyFromISO(r.start_date)
    const end = dateOnlyFromISO(r.end_date)
    if (!start || !end) return false
    const daysUntilStart = Math.floor((start - today) / (1000 * 60 * 60 * 24))
    const requiresCaution = ['booking', 'direct'].includes(r.source)
    switch (statut) {
      case 'a-relancer-paiement':
        return requiresCaution && !r.payment_received_at && daysUntilStart >= 0 && daysUntilStart < 4
      case 'caution-ko':
        return requiresCaution && !!r.payment_received_at && !r.caution_received_at
      case 'arrivee-today':
        return start.getTime() === today.getTime()
      case 'depart-today':
        return end.getTime() === today.getTime()
      case 'sur-place':
        return start <= today && today < end
      case 'a-venir-7j':
        return daysUntilStart > 0 && daysUntilStart <= 7
      default:
        return true
    }
  })
}

function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// NQAb-2 fix — `invalidatePricingBlocks` (hissé App.jsx via usePricingBlocksHistory)
// propagé pour invalider l'historique pricing_blocks "résa direct interne" après
// mutation (release/push/updated via ResaV2Modal kind='pricing_block', ou create
// via UnifiedDateActionModal onglet "Bloquer"). Sans ça, la Compta/Annuel reste
// désynchro tant que l'app n'est pas remountée.
export default function CalendarPanel({ cfg, invalidatePricingBlocks }) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d
  })
  const [reservations, setReservations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // PR #169a-bis (ADR-105) — state unifié `selectedItem: {id, kind}` pour
  // ResaV2Modal kind-aware (résa OU pricing_block).
  const [selectedItem, setSelectedItem] = useState(null)
  const [filterLogement, setFilterLogement] = useState('all')
  const [filterSource, setFilterSource] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatut, setFilterStatut] = useState('all')

  // Phase 6.B PR #167a (ADR-099) + PR #167b amend (ADR-100) — modal unifiée
  // actions de date du calendrier.
  // unifiedModal : { open: true, mode: 'prix'|'bloquer', logementId?, dateISO?, lockTab? }
  //  - clic cellule manual → mode='prix', PAS de lockTab → onglets switchables
  //    (user peut basculer Prix ↔ Bloquer sans fermer la modal).
  //  - bouton header → mode='bloquer', lockTab='bloquer' → onglets masqués
  //    (le bouton "🔒 Bloquer une période" lance l'action explicite et conserve
  //    le comportement legacy : pas de switch possible).
  // L'invalidate est toujours globalInvalidate (BPRa-2 : source unique).
  const [unifiedModal, setUnifiedModal] = useState(null)
  const [pricingToast, setPricingToast] = useState(null) // { kind, msg }
  // R2-UI (ADR-256) — divergence Beds24 sélectionnée (clic barre hachurée).
  // { logementId, logementLabel, startISO, endISO } | null.
  const [beds24Divergence, setBeds24Divergence] = useState(null)
  // Micro-UX M14 — segments dont la réouverture Beds24 vient d'être envoyée
  // (mode `rouvrir` résolu avec succès, hors DRY_RUN). Le miroir
  // daily_pricing.num_avail restant stale jusqu'au webhook/cron, le segment
  // hachuré persisterait malgré globalInvalidate → état OPTIMISTE purement
  // visuel (session) : TableauView le rend gris « Réouverture envoyée ✓ »,
  // non cliquable. [{logementId, start, end}] bornes ISO incluses.
  // MINEUR-1 revue #993 : purgé dès que le miroir confirme (effet ci-dessous),
  // l'optimiste ne couvre QUE la fenêtre stale.
  const [beds24ReopenedSegments, setBeds24ReopenedSegments] = useState([])
  // PR #169a-bis (ADR-105) — blockDetailsModal SUPPRIMÉ. Les blocks
  // ouvrent désormais ResaV2Modal via selectedItem={id, kind:'pricing_block'}.
  // DRY_RUN flag affichage : tiré au mount via /pricing_logement_config GET. Default true (safe).
  // Lot AUTO-REFRESH (QW6 « config ») : seedé depuis le cache session (retour
  // d'onglet = valeur immédiate, pas de flash DRY_RUN erroné), puis TOUJOURS
  // refetché en fond — le cache est mis à jour à chaque réponse.
  const [pricingDryRunActive, setPricingDryRunActive] = useState(
    () => swrGet('cal|dry_run_active')?.value ?? true,
  )
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        const res = await fetch('/api/beds24-mapping?action=pricing_logement_config', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        })
        if (!res.ok) return
        const json = await res.json()
        const active = json.dry_run_active !== false
        swrSet('cal|dry_run_active', active)
        if (!cancelled) setPricingDryRunActive(active)
      } catch (_) { /* swallow — modal restera DRY_RUN safe par défaut */ }
    })()
    return () => { cancelled = true }
  }, [])

  // Dispatch clic cellule calendrier (Tableau + Mensuel).
  // Phase 6.B PR #167a — Si block fourni → ResaV2Modal kind=pricing_block.
  // PR #167b amend (ADR-100) — clic cellule mode='manual' → UnifiedDateActionModal
  // initialTab='prix' SANS lockTab : onglets visibles + switchables. User peut
  // basculer vers "🔒 Bloquer" depuis la même modal. logementId + dateISO
  // pré-fillent les deux bodies (Prix direct + Bloquer via pass-through).
  // `invalidate` reçu est ignoré (BPRa-2 : on appelle directement globalInvalidate
  // dans le callback onSaved — source unique).
  // PR #169a-bis (ADR-105) — block ouvre ResaV2Modal unifié (plus de modal Details séparé).
  function handleCellClick({ logementId, dateISO, mode, block }) {
    // PR #169a-bis : priorité au block (cellule bloquée) → ouvre ResaV2Modal.
    if (block) {
      setSelectedItem({ id: block.id, kind: 'pricing_block' })
      return
    }
    if (mode === 'manual') {
      // PR #167b amend — pas de lockTab → onglets switchables.
      setUnifiedModal({ open: true, mode: 'prix', logementId, dateISO })
    } else if (mode === 'pricelabs') {
      // TODO PR4 : tooltip avec lien externe PriceLabs (cfg.pricelabs_url).
      // V1 simple : toast informatif.
      setPricingToast({ kind: 'info', msg: 'Mode pricelabs : éditer le prix sur PriceLabs.app' })
      setTimeout(() => setPricingToast(null), 4000)
    } else {
      setPricingToast({ kind: 'info', msg: 'Logement non configuré pour pricing — voir Paramètres > 🔌 Beds24.' })
      setTimeout(() => setPricingToast(null), 4000)
    }
  }

  // Phase 6.B PR #167a — bouton header → UnifiedDateActionModal mode='bloquer'.
  // PR #167b amend : lockTab='bloquer' explicite → onglets masqués (action
  // dédiée "Bloquer une période" depuis header conserve le UX inchangé,
  // contrairement au clic cellule qui révèle les onglets switchables).
  const handleOpenBlockCreate = useCallback(() => {
    setUnifiedModal({ open: true, mode: 'bloquer', lockTab: 'bloquer' })
  }, [])

  // Phase 2.G.6 — toggle vue persisté
  // PR #169d — safeViewMode() route 'mensuel' legacy → 'tableau' (fallback).
  // PR #169c — 'liste' ajouté allowlist.
  const [viewMode, setViewMode] = useState(() => {
    try { return safeViewMode(localStorage.getItem(VIEW_LS_KEY)) }
    catch (_) { return 'tableau' }
  })
  useEffect(() => {
    try { localStorage.setItem(VIEW_LS_KEY, viewMode) } catch (_) {}
  }, [viewMode])

  // PR #169c (H-2 amend v1.1) — count Liste piloté par ListeView via
  // onCountChange. Le footer global CalendarPanel utilise listeCount quand
  // viewMode='liste' (vs filtered.length pour les autres vues qui n'ajoutent
  // pas de blocks). Évite incohérence "47 résas" vs Liste 52 lignes.
  const [listeCount, setListeCount] = useState(0)

  // PR #167b (ADR-100) — toggle "📐 Min nuits" persisté (default OFF).
  // ON → cellules affichent `min Nn` 2e ligne sous le prix (Option A : tout
  // daily_pricing.min_stay, pas distinction override admin vs seed Beds24).
  const [showMinStay, setShowMinStay] = useState(() => {
    try { return localStorage.getItem(SHOW_MIN_STAY_LS_KEY) === 'true' }
    catch (_) { return false }
  })
  useEffect(() => {
    try { localStorage.setItem(SHOW_MIN_STAY_LS_KEY, showMinStay ? 'true' : 'false') } catch (_) {}
  }, [showMinStay])

  // Légende repliable (affichage pur) — repliée par défaut pour ne pas
  // encombrer la grille dense. Non persistée (état purement local de session).
  const [showLegend, setShowLegend] = useState(false)

  // Fantômes calendrier (décision Dimitri 2026-07-20) — interrupteur
  // « Afficher les fantômes ci-dessus » (case dans la Légende). Défaut COCHÉ,
  // persisté localStorage. Décoché → aucun fantôme rendu (filtre 100 % client :
  // le payload cancelled_blocks continue d'être servi).
  const [showCancelled, setShowCancelled] = useState(() => {
    try { return parseShowCancelled(localStorage.getItem(SHOW_CANCELLED_LS_KEY)) }
    catch (_) { return true }
  })
  useEffect(() => {
    try { localStorage.setItem(SHOW_CANCELLED_LS_KEY, showCancelled ? 'true' : 'false') } catch (_) {}
  }, [showCancelled])

  // getAllLogsFlat descend dans chambres feuilles pour la sous-loc (Maison Zen
  // → lc1/lc2/lc3 affichées séparément au lieu du log parent). Pas de filtre
  // Beds24-active : les logements sans mapping affichent une ligne vide,
  // utile pour visualiser ce qui reste à mapper.
  const allLogs = useMemo(() => getAllLogsFlat(cfg)
    .map(l => ({ id: l.id, nom: l.nom_complet || l.nom, prop: l.propNom })), [cfg])

  // Map id → nom pour tooltips (LPRv3-2 + PR #169d TodayView labels)
  const logementsById = useMemo(() => {
    const m = new Map()
    allLogs.forEach(l => m.set(l.id, l.nom))
    return m
  }, [allLogs])

  // Phase 6.B PR5A — hook unique hissé au CalendarPanel parent (fix N-QA-1).
  // Avant : TableauView (+ ex-MensuelView) ré-instanciaient leur propre hook
  // avec leur propre cacheRef → globalInvalidate() après Create ne touchait
  // que le cache du hook CalendarPanel, pas celui des vues enfants →
  // cellules grisées n'apparaissaient qu'après nav mois. Solution :
  // 1 seule instance ici, props drillées vers TableauView (+ TodayView qui
  // consomme blocksByLogement pour rotations PR #169d).
  // Range = mois courant +/- 7j (couvre monthDays TableauView).
  const globalLogementIds = useMemo(() => allLogs.map((l) => l.id), [allLogs])
  const globalDateRange = useMemo(() => {
    const lo = new Date(currentMonth); lo.setDate(lo.getDate() - 7)
    const hi = new Date(currentMonth); hi.setMonth(hi.getMonth() + 1); hi.setDate(hi.getDate() + 7)
    return { from: toISODate(lo), to: toISODate(hi) }
  }, [currentMonth])
  const {
    pricingMap: globalPricingMap,
    modesByLogement: globalModesByLogement,
    blocksByLogement: globalBlocksByLogement,
    cancelledBlocksByLogement: globalCancelledBlocksByLogement, // Fantômes calendrier
    minStayDefaultsByLogement: globalMinStayDefaultsByLogement, // PR #167c (ADR-101 D-8)
    invalidate: globalInvalidate,
  } = useDailyPricing(globalLogementIds, globalDateRange)

  // MINEUR-1 revue #993 — purge des segments optimistes M14 au refresh du
  // miroir : dès que globalPricingMap montre les dates rouvertes (plus aucun
  // num_avail=0 sur le segment), l'entrée optimiste est obsolète — un NOUVEAU
  // blocage Beds24 légitime sur les mêmes dates doit re-hachurer NORMALEMENT.
  // pruneReopenedSegments renvoie la même référence si rien à purger → le
  // setState est un no-op React (pas de boucle de rendu).
  useEffect(() => {
    setBeds24ReopenedSegments((prev) => pruneReopenedSegments(prev, globalPricingMap))
  }, [globalPricingMap])

  // LOT 5 — alerte de désynchronisation : liste des push outbox NON confirmés
  // (action read-only pricing_outbox_pending_read). Cas nominal = outbox vide
  // → aucun encart, aucun ⚠️. Re-fetché quand globalInvalidate change (après un
  // push manuel / drain) via refreshOutboxTick.
  // Lot AUTO-REFRESH (QW6) : seedé depuis le cache session (retour d'onglet =
  // encart stable immédiat) + refetch en fond systématique (cache mis à jour).
  const [outboxPendingItems, setOutboxPendingItems] = useState(
    () => swrGet('cal|outbox_pending')?.value || [],
  )
  const [refreshOutboxTick, setRefreshOutboxTick] = useState(0)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        const res = await fetch('/api/beds24-mapping?action=pricing_outbox_pending_read', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        })
        if (!res.ok) return
        const json = await res.json()
        const items = Array.isArray(json.items) ? json.items : []
        swrSet('cal|outbox_pending', items)
        if (!cancelled) setOutboxPendingItems(items)
      } catch (_) { /* swallow — l'encart restera simplement masqué */ }
    })()
    return () => { cancelled = true }
  }, [refreshOutboxTick])

  const outboxPendingByLogement = useMemo(
    () => buildOutboxPendingByLogement(outboxPendingItems),
    [outboxPendingItems],
  )
  const outboxPendingCount = countOutboxPending(outboxPendingItems)
  // Re-fetch l'outbox après un push/release/update (un drain a pu vider/remplir
  // la file). Appelé conjointement à globalInvalidate.
  const refreshOutbox = useCallback(() => setRefreshOutboxTick((t) => t + 1), [])

  // Lot AUTO-REFRESH (QW6) — stale-while-revalidate : au retour d'onglet, la
  // fenêtre déjà vue est rendue IMMÉDIATEMENT depuis le cache session, puis le
  // fetch met à jour en fond (le premier chargement d'une fenêtre reste
  // identique : spinner). `silent=true` strict (pattern InboxPanel) = passage
  // AUTO (realtime) — pas de loading, échec silencieux qui garde la vue.
  const loadResas = useCallback(async (silent) => {
    const lo = new Date(currentMonth); lo.setDate(lo.getDate() - 7)
    const hi = new Date(currentMonth); hi.setMonth(hi.getMonth() + 1); hi.setDate(hi.getDate() + 7)
    const cacheKey = `cal|resas|${toISODate(lo)}|${toISODate(hi)}`
    const cached = swrGet(cacheKey)
    // Revalidation en fond : soit passage auto (silent), soit cache affichable.
    const background = silent === true || !!cached
    if (cached && silent !== true) {
      setReservations(cached.value)
      setLoading(false)
    }
    if (!background) { setLoading(true); setError(null) }
    try {
      const { data, error: e } = await supabase
        .from('reservations')
        // PR #169d F-1 : `arrival_time` ajouté pour vue Aujourd'hui (carte
        // check-in affiche l'heure d'arrivée). Champ existant Beds24 v2.
        // Lot heures voyageur (ADR-226) : `guest_arrival_time` /
        // `guest_departure_time` (dernière soumission du formulaire de
        // bienvenue, colonnes TIME) alimentent les puces GuestTimesPills sur
        // chaque mouvement de la vue Aujourd'hui admin. Surface ADMIN only
        // (CalendarPanel monté sous CATEGORIES isAdmin — ménage = MenageTodayView).
        // PR #169c : `guest_email` (recherche email Liste) + `created_at`
        // (colonne tri Liste "Date résa"). Commit 5 fix régression : la
        // colonne `booking_date` n'existe pas dans `reservations` (smoke prod
        // erreur 42703). `created_at` est la vraie colonne métier "date résa"
        // (TIMESTAMPTZ DEFAULT NOW() — migration 20260502000001). Pattern
        // cohérent ResaV2Modal:713 (booking_date fallback created_at).
        .select('id, guest_first_name, guest_last_name, guest_email, source, status, logement_id, start_date, end_date, created_at, total_amount, guest_count, payment_received_at, caution_received_at, arrival_time, guest_arrival_time, guest_departure_time')
        .gte('end_date', toISODate(lo))
        .lte('start_date', toISODate(hi))
        .order('start_date', { ascending: true })
      if (e) throw e
      const rows = data || []
      swrSet(cacheKey, rows)
      setReservations(rows)
      // m1 (passe corrective) : un refetch de fond qui RÉUSSIT efface aussi
      // une éventuelle bannière d'erreur antérieure — sinon elle resterait
      // affichée au-dessus de données pourtant fraîches.
      setError(null)
    } catch (e) {
      // En fond : on garde la dernière vue valide (pas de bannière qui
      // scintille sur un passage auto) — en chargement visible : inchangé.
      if (background) console.error('[calendar] refresh résas silencieux en échec:', e.message)
      else setError(e.message)
    }
    finally { setLoading(false) }
  }, [currentMonth])

  useEffect(() => { loadResas() }, [loadResas])

  // Lot AUTO-REFRESH — realtime « signal → refetch » calendrier : un événement
  // sur reservations (sync Beds24, annulation, paiement) refetch la fenêtre en
  // silencieux ; un événement sur pricing_blocks (bloc créé/libéré/résa direct)
  // invalide les caches pricing (batch_read + historique blocks App-level).
  // Debounce ≥2 s (une rafale de sync = UN refetch), pause onglet masqué,
  // fallback : sans publication realtime, rien ne se passe ici et les
  // mécaniques existantes (refetch au mount/mois, invalidate post-action)
  // restent seules à jouer — comportement historique.
  useRealtimeRefresh(
    ['reservations', 'pricing_blocks'],
    useCallback((changed) => {
      if (changed.includes('reservations')) loadResas(true)
      if (changed.includes('pricing_blocks')) {
        try { globalInvalidate?.() } catch (_) {}
        try { invalidatePricingBlocks?.() } catch (_) {}
      }
    }, [loadResas, globalInvalidate, invalidatePricingBlocks]),
  )

  const today = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d
  }, [])

  // W1 — navigation jour dans la vue "Aujourd'hui", bornée au mois affiché.
  const [selectedDay, setSelectedDay] = useState(today)
  // D1 : au changement de MOIS affiché, recaler le jour sélectionné —
  //   mois courant → aujourd'hui ; autre mois → 1er du mois.
  // (Ne se déclenche QUE sur changement de currentMonth ; navDay ne touche
  //  que selectedDay, donc ce reset ne s'applique pas à la nav jour.)
  useEffect(() => {
    const isCur = currentMonth.getFullYear() === today.getFullYear()
      && currentMonth.getMonth() === today.getMonth()
    if (isCur) {
      setSelectedDay(today)
    } else {
      const d = new Date(currentMonth); d.setHours(0, 0, 0, 0); setSelectedDay(d)
    }
  }, [currentMonth, today])
  // navDay : ±1 jour, STRICTEMENT borné au mois affiché (pas de débordement).
  function navDay(delta) {
    setSelectedDay(prev => {
      const next = new Date(prev); next.setDate(next.getDate() + delta); next.setHours(0, 0, 0, 0)
      if (next.getMonth() !== currentMonth.getMonth() || next.getFullYear() !== currentMonth.getFullYear()) {
        return prev // hors du mois affiché → no-op (bornage)
      }
      return next
    })
  }

  const filtered = useMemo(() => {
    let r = reservations
    if (filterLogement !== 'all') r = r.filter(x => x.logement_id === filterLogement)
    if (filterSource !== 'all')   r = r.filter(x => x.source === filterSource)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      r = r.filter(x => `${x.guest_first_name || ''} ${x.guest_last_name || ''}`.toLowerCase().includes(q))
    }
    if (filterStatut !== 'all') r = applyStatutFilter(r, filterStatut, today)
    return r
  }, [reservations, filterLogement, filterSource, searchQuery, filterStatut, today])

  // Compteur du pied de page (vues Tableau / Aujourd'hui) — voir plus bas.
  // `filtered` conserve les annulées (la vue Liste et le filtre statut
  // « Annulée » en ont besoin) ; seul le COMPTE affiché les retire.
  // Point 34 passe anti-plantage : une résa d'un logement inconnu de cfg n'est
  // pas dessinée par TableauView → on l'exclut aussi du compte (cohérence
  // compteur/dessin — seul changement visible de la passe).
  const activeResaCount = useMemo(
    () => countActiveResas(filtered, new Set(globalLogementIds)),
    [filtered, globalLogementIds],
  )

  function navMonth(delta) {
    setCurrentMonth(prev => {
      const next = new Date(prev); next.setMonth(next.getMonth() + delta); return next
    })
  }
  function goToday() {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); setCurrentMonth(d)
  }

  const monthLabel = `${MONTHS[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`

  // W1 — bornes nav jour (vue Aujourd'hui) : on ne sort jamais du mois affiché.
  const lastDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate()
  const canPrevDay = selectedDay.getDate() > 1
  const canNextDay = selectedDay.getDate() < lastDayOfMonth
  const isTodaySelected = selectedDay.getFullYear() === today.getFullYear()
    && selectedDay.getMonth() === today.getMonth()
    && selectedDay.getDate() === today.getDate()

  return (
    <div style={{fontFamily:DS.fontBody}}>
      {/* Header avec navigation + filtres + toggle vue */}
      <div style={{background:DS.white,borderRadius:12,padding:"14px 18px",marginBottom:14,boxShadow:"0 2px 8px rgba(0,0,0,.06)",display:"flex",justifyContent:"space-between",alignItems:"center",gap:14,flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <div style={{display:"inline-flex",alignItems:"center",gap:4,background:DS.cielSoft,borderRadius:999,padding:"4px 6px",maxWidth:"100%"}}>
            <button onClick={() => navMonth(-1)} aria-label="Mois précédent" className="fbz-tap fbz-press"
              style={{background:"none",color:DS.marine,border:"none",borderRadius:8,padding:"5px 7px",display:"flex",alignItems:"center",cursor:"pointer"}}>
              <ChevronLeft size={18} strokeWidth={2.2} /></button>
            <div style={{fontFamily:DS.fontDisplay,fontSize:20,fontWeight:600,color:DS.marine,minWidth:160,textAlign:"center"}}>{monthLabel}</div>
            <button onClick={() => navMonth(1)} aria-label="Mois suivant" className="fbz-tap fbz-press"
              style={{background:"none",color:DS.marine,border:"none",borderRadius:8,padding:"5px 7px",display:"flex",alignItems:"center",cursor:"pointer"}}>
              <ChevronRight size={18} strokeWidth={2.2} /></button>
          </div>
          <button onClick={goToday} className="fbz-tap fbz-press"
            style={{marginLeft:6,background:DS.marine,color:"#fff",border:"none",borderRadius:9,padding:"7px 13px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:DS.fontBody}}>
            Aujourd'hui
          </button>
          {/* Phase 6.B PR5A (D-2) — bouton "Bloquer une période" (DA : corail = action). */}
          <button onClick={handleOpenBlockCreate} className="fbz-press"
            title="Bloquer une période (travaux, visite proprio, indispo)"
            style={{marginLeft:6,background:DS.corail,color:"#fff",border:"none",borderRadius:9,padding:"7px 13px",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6,fontFamily:DS.fontBody}}>
            <Lock size={14} strokeWidth={2.2} /> Bloquer une période
          </button>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",minWidth:0}}>
          {/* Recherche voyageur (Phase 2.G.6 #14) — DA : icône Search, fond blanc. */}
          <div style={{position:"relative",display:"flex",alignItems:"center",minWidth:0}}>
            <Search size={14} strokeWidth={2} color={DS.textMuted}
              style={{position:"absolute",left:9,pointerEvents:"none"}} aria-hidden="true"/>
            <input type="search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Rechercher voyageur…"
              style={{border:`1px solid ${DS.border}`,borderRadius:9,padding:"7px 10px 7px 28px",fontSize:12,background:DS.white,width:200,maxWidth:"100%",outline:"none",fontFamily:DS.fontBody,color:DS.textPrimary}}/>
          </div>
          <select value={filterLogement} onChange={e => setFilterLogement(e.target.value)}
            style={{border:`1px solid ${DS.border}`,borderRadius:9,padding:"7px 10px",fontSize:12,background:DS.white,minWidth:0,maxWidth:"100%",color:DS.textPrimary,fontFamily:DS.fontBody}}>
            <option value="all">Tous les logements</option>
            {allLogs.map(l => <option key={l.id} value={l.id}>{l.id} · {l.nom}</option>)}
          </select>
          <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
            style={{border:`1px solid ${DS.border}`,borderRadius:9,padding:"7px 10px",fontSize:12,background:DS.white,minWidth:0,maxWidth:"100%",color:DS.textPrimary,fontFamily:DS.fontBody}}>
            <option value="all">Toutes les sources</option>
            <option value="airbnb">Airbnb</option>
            <option value="booking">Booking</option>
            <option value="direct">Direct</option>
          </select>
          {/* Filtre rapide statut métier (Phase 2.G.6 #11)
              PR #169d F-6 — désactivé en vue Aujourd'hui (la vue gère son
              propre filtrage temporel ; valeur préservée pour retour Tableau). */}
          <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}
            disabled={viewMode === 'aujourdhui'}
            title={viewMode === 'aujourdhui'
              ? "Inutile en vue Aujourd'hui (filtre status='cancelled' déjà appliqué)"
              : "Filtre rapide par statut métier"}
            style={{
              border:`1px solid ${filterStatut === 'all' ? DS.border : '#f1d9ac'}`,borderRadius:9,padding:"7px 10px",fontSize:12,
              background:filterStatut === 'all' ? DS.white : DS.warningBg,
              color: filterStatut === 'all' ? DS.textPrimary : DS.warningText,
              fontFamily:DS.fontBody,
              opacity: viewMode === 'aujourdhui' ? 0.5 : 1,
              cursor: viewMode === 'aujourdhui' ? 'not-allowed' : 'pointer',
              minWidth:0,maxWidth:"100%",
            }}>
            {STATUT_FILTERS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          {/* PR #167b — toggle "Min nuits" (DA : icône Ruler). */}
          <button
            onClick={() => setShowMinStay(!showMinStay)}
            title={showMinStay ? 'Masquer min nuits dans les cellules' : 'Afficher min nuits dans les cellules'}
            data-testid="toggle-show-min-stay"
            className="fbz-press"
            style={{
              background: showMinStay ? DS.marine : "#eef2f7",
              color: showMinStay ? "#fff" : DS.textSecondary,
              border:"none",borderRadius:9,padding:"7px 11px",fontSize:12,
              fontWeight: 600,
              cursor:"pointer",fontFamily:DS.fontBody,whiteSpace:"nowrap",
              marginLeft:6,display:"flex",alignItems:"center",gap:6,
            }}>
            <Ruler size={14} strokeWidth={2} /> Min nuits
          </button>
          {/* Toggle vue (Phase 2.G.6) — DA : icône + libellé, actif marine. */}
          <div style={{display:"flex",gap:2,marginLeft:6}}>
            {VIEWS.map(v => {
              const ViewIcon = v.Icon
              const active = viewMode === v.id
              return (
                <button key={v.id}
                  onClick={() => v.enabled && setViewMode(v.id)}
                  disabled={!v.enabled}
                  title={v.enabled ? `Vue ${v.label}` : 'Phase ultérieure'}
                  className="fbz-press"
                  style={{
                    background: active ? DS.marine : "#eef2f7",
                    color: active ? "#fff" : (v.enabled ? DS.textSecondary : DS.textMuted),
                    border:"none",borderRadius:9,padding:"7px 11px",fontSize:12,
                    fontWeight: 600,
                    cursor: v.enabled ? "pointer" : "not-allowed",
                    opacity: v.enabled ? 1 : 0.5,
                    fontFamily:DS.fontBody,whiteSpace:"nowrap",
                    display:"flex",alignItems:"center",gap:6,
                  }}>
                  <ViewIcon size={14} strokeWidth={2} /> {v.label}
                </button>
              )
            })}
          </div>
          {/* Légende repliable (affichage pur) — bouton dans le header,
              panneau déplié sous le header. Repliée par défaut. */}
          <CalendarLegend open={showLegend} onToggle={() => setShowLegend(v => !v)} />
        </div>
      </div>

      {showLegend && (
        <CalendarLegend.Panel
          showCancelled={showCancelled}
          onToggleCancelled={() => setShowCancelled(v => !v)}
        />
      )}

      {/* LOT 5 — encart alerte désynchro tarifaire : N'AFFICHE RIEN si 0 push
          non confirmé (cas nominal, outbox vide). Visible en vue Tableau (où
          les ⚠️ par case existent). Au survol : liste compacte des entrées. */}
      {viewMode === 'tableau' && outboxPendingCount > 0 && (
        <div
          data-testid="outbox-pending-banner"
          title={outboxPendingItems
            .map((it) => `${it.logement_id} ${it.date_from}→${it.date_to || it.date_from} (${it.status})${it.last_error ? ' : ' + it.last_error : ''}`)
            .join('\n')}
          style={{
            display:"flex",alignItems:"center",gap:9,
            background:DS.warningBg,border:"0.5px solid #f1d9ac",borderLeft:`3px solid ${DS.warning}`,borderRadius:9,
            padding:"10px 14px",marginBottom:12,
            fontSize:13,fontWeight:600,color:DS.warningText,fontFamily:DS.fontBody,
          }}
        >
          <AlertTriangle size={16} strokeWidth={2} color={DS.warning} aria-hidden="true" style={{flexShrink:0}} />
          {/* Libellé volontairement générique : la file `pricing_outbox` n'est
              PAS que du tarif. handlePricingOutboxPendingRead (lib/pricing-
              handlers.js) ne filtre pas `route_type`, or la colonne porte
              aussi 'block_create' et 'block_release' — la pose et la levée
              d'un BLOCAGE de période. Annoncer « push tarif » faisait passer
              un blocage en attente pour un problème de prix. Le libellé est
              corrigé ici ; le handler n'est pas touché (l'encart doit bien
              surfacer toute la file, pas seulement les prix). */}
          <span>
            {outboxPendingCount} synchro{outboxPendingCount > 1 ? 's' : ''} Beds24 non confirmée{outboxPendingCount > 1 ? 's' : ''}
            <span style={{fontWeight:400,marginLeft:6}}>
              — prix ou blocage de période, en attente (les dates concernées portent un marqueur)
            </span>
          </span>
        </div>
      )}

      {/* Body : render conditionnel */}
      <div style={{background:DS.white,borderRadius:12,padding:viewMode === 'tableau' ? 4 : 18,boxShadow:"0 2px 8px rgba(0,0,0,.06)"}}>
        {error && <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",background:DS.dangerBg,color:DS.dangerText,borderLeft:`3px solid ${DS.danger}`,borderRadius:8,fontSize:12,marginBottom:10}}><AlertTriangle size={15} strokeWidth={2} color={DS.danger} style={{flexShrink:0}} /> {error}</div>}

        {viewMode === 'tableau' && (
          <TableauView
            currentMonth={currentMonth}
            today={today}
            allLogs={allLogs}
            filtered={filtered}
            onSelectResa={(id) => setSelectedItem({ id, kind: 'reservation' })}
            onCellClick={handleCellClick}
            pricingMap={globalPricingMap}
            modesByLogement={globalModesByLogement}
            blocksByLogement={globalBlocksByLogement}
            // Fantômes calendrier — toggle OFF → null (aucun fantôme rendu,
            // filtre client ; le payload continue de servir cancelled_blocks).
            cancelledBlocksByLogement={showCancelled ? globalCancelledBlocksByLogement : null}
            minStayDefaultsByLogement={globalMinStayDefaultsByLogement} // PR #167c
            outboxPendingByLogement={outboxPendingByLogement} // LOT 5
            invalidate={globalInvalidate}
            showMinStay={showMinStay}
            onBeds24Divergence={setBeds24Divergence} // R2-UI (ADR-256)
            beds24ReopenedSegments={beds24ReopenedSegments} // Micro-UX M14
          />
        )}

        {viewMode === 'aujourdhui' && (
          <TodayView
            today={today}
            selectedDay={selectedDay}
            isToday={isTodaySelected}
            onPrevDay={() => navDay(-1)}
            onNextDay={() => navDay(1)}
            canPrevDay={canPrevDay}
            canNextDay={canNextDay}
            filtered={filtered}
            blocksByLogement={globalBlocksByLogement}
            logementsById={logementsById}
            onSelectItem={setSelectedItem}
          />
        )}

        {viewMode === 'liste' && (
          <ListeView
            filtered={filtered}
            blocksByLogement={globalBlocksByLogement}
            logementsById={logementsById}
            onSelectItem={setSelectedItem}
            onCountChange={setListeCount}
          />
        )}

        {pricingToast && (
          <div role="alert" style={{
            position:"fixed", bottom:24, right:24,
            padding:"10px 14px", borderRadius:8, fontSize:12, zIndex:1100,
            background:"#eff6ff", color:DS.navy, border:"1px solid #bfdbfe",
            boxShadow:"0 4px 12px rgba(0,0,0,.15)",
          }}>{pricingToast.msg}</div>
        )}

        <div style={{marginTop:14,fontSize:11,color:"#888",textAlign:"right"}}>
          {loading
            ? 'Chargement…'
            : viewMode === 'liste'
              // PR #169c H-2 — count Liste incluant blocks direct interne
              // (vs filtered.length qui ne contient que des reservations).
              ? `${listeCount} ligne${listeCount > 1 ? 's' : ''} sur la fenêtre ${monthLabel}`
              // Compte les résas RÉELLEMENT dessinées : Tableau et Aujourd'hui
              // retirent tous deux les annulées (filterActiveResas côté
              // TableauView, buildMovementsFromSources côté Aujourd'hui). AVANT :
              // `filtered.length` les incluait → le compteur annonçait plus de
              // résas que le tableau n'en dessinait. La vue Liste garde son
              // propre compteur (listeCount) car elle affiche EN PLUS les blocs.
              : `${activeResaCount} résa${activeResaCount > 1 ? 's' : ''} sur la fenêtre ${monthLabel}`}
        </div>
      </div>

      {/* PR #169a-bis (ADR-105) — ResaV2Modal unifié résa + pricing_block.
          - selectedItem.kind='reservation' : reservations (clic cellule sous résa)
          - selectedItem.kind='pricing_block' : pricing_blocks (clic cellule bloc)
          onItemAction : invalidate caches calendrier post-action (release/push/edit). */}
      {selectedItem && (
        <ResaV2Modal
          itemId={selectedItem.id}
          itemKind={selectedItem.kind}
          cfg={cfg}
          dryRunActive={pricingDryRunActive}
          onClose={() => {
            setSelectedItem(null)
            if (selectedItem.kind === 'reservation') loadResas()
          }}
          onItemAction={(type, result) => {
            // 'released' / 'pushed' / 'updated' (LPR169a-bis-5)
            try { globalInvalidate?.() } catch (_) {}
            refreshOutbox() // LOT 5 — re-fetch file outbox après action
            // NQAb-2 fix — invalide aussi l'historique pricing_blocks "résa direct
            // interne" (Compta/Annuel) après release/push/updated d'un block voyageur.
            try { invalidatePricingBlocks?.() } catch (_) {}
            if (type === 'released') {
              setPricingToast({
                kind: 'success',
                msg: result.dry_run
                  ? 'DRY_RUN : block libéré ✅'
                  : 'Période débloquée Beds24 ✅',
              })
              setTimeout(() => setPricingToast(null), 4000)
            } else if (type === 'pushed') {
              setPricingToast({
                kind: 'success',
                msg: result.dry_run
                  ? 'DRY_RUN : push simulé ✅'
                  : 'Block poussé Beds24 ✅',
              })
              setTimeout(() => setPricingToast(null), 4000)
            }
            // 'updated' : silent invalidate (toast déjà géré par body via setError/info).
          }}
        />
      )}

      {/* Phase 6.B PR #167a (ADR-099) + PR #167b amend (ADR-100) — UnifiedDateActionModal Option Hybride.
          Remplace PricingManualEditModal + PricingBlockCreateModal (qui restent
          en thin wrapper pour rétro-compat).
          - Clic cellule manual → lockTab=null → onglets visibles + switchables.
          - Bouton header "🔒 Bloquer une période" → lockTab='bloquer' → onglets masqués.
          Le toast post-save lit `result.mode` (envoyé par le body) car
          unifiedModal.mode reflète l'initialTab, pas l'onglet actif au submit
          (l'user peut avoir switché). */}
      <UnifiedDateActionModal
        open={!!unifiedModal?.open}
        initialTab={unifiedModal?.mode || 'prix'}
        lockTab={unifiedModal?.lockTab || null}
        logementId={unifiedModal?.logementId}
        logementLabel={
          unifiedModal?.logementId
            ? logementsById.get(unifiedModal.logementId)
            : ''
        }
        dateISO={unifiedModal?.dateISO}
        allLogs={allLogs}
        modesByLogement={globalModesByLogement}
        pricingMap={globalPricingMap}
        dryRunActive={pricingDryRunActive}
        invalidate={globalInvalidate}
        onClose={() => setUnifiedModal(null)}
        onSaved={(result) => {
          // BPRa-2 : source unique d'invalidate (globalInvalidate hissé).
          // Unifie les 2 callbacks save (Prix + Bloquer) en un seul chemin.
          try { globalInvalidate?.() } catch (_) {}
          refreshOutbox() // LOT 5 — re-fetch file outbox après un set prix manuel
          // NQAb-2 fix — onglet "Bloquer" peut créer un pricing_block avec
          // guest_first_name (résa direct interne) → invalider l'historique
          // pour que Compta/Annuel reflète immédiatement. Pour onglet "Prix"
          // (set_manual), l'appel est no-op côté donnée (pas d'impact blocks)
          // mais le coût refetch est négligeable (qq dizaines de rows).
          try { invalidatePricingBlocks?.() } catch (_) {}
          // PR #167b amend : result.mode renvoyé par le body actif au submit
          // (Prix → 'prix', Bloquer → 'bloquer'). Fallback unifiedModal.mode
          // pour rétro-compat si body ne propage pas (defensive).
          const savedMode = result?.mode || unifiedModal?.mode
          const isBlock = savedMode === 'bloquer'
          setPricingToast({
            kind: 'success',
            msg: result.dry_run
              ? isBlock ? 'DRY_RUN : block créé ✅' : 'DRY_RUN : audit logué ✅'
              : isBlock ? 'Période bloquée Beds24 ✅' : 'Prix poussé Beds24 ✅',
          })
          setTimeout(() => setPricingToast(null), 4000)
          setUnifiedModal(null)
        }}
      />

      {/* PR #169a-bis (ADR-105) — PricingBlockDetailsModal SUPPRIMÉ.
          Les blocks ouvrent désormais ResaV2Modal kind='pricing_block' ci-dessus. */}

      {/* R2-UI (ADR-256) — modale « Divergence Beds24 » : action sur une barre
          hachurée « Bloqué sur Beds24 ». Succès → invalidation des caches
          calendrier (num_avail + blocks) + toast, comme les autres actions. */}
      <Beds24DivergenceModal
        open={!!beds24Divergence}
        logementId={beds24Divergence?.logementId}
        logementLabel={beds24Divergence?.logementLabel}
        startISO={beds24Divergence?.startISO}
        endISO={beds24Divergence?.endISO}
        onClose={() => setBeds24Divergence(null)}
        onResolved={(result) => {
          // Source unique d'invalidate (globalInvalidate hissé) + blocks +
          // outbox. « reprendre » crée un pricing_block (→ invalidatePricingBlocks
          // fait disparaître la hachure au profit d'une barre FBZ). « rouvrir »
          // pousse num_avail=1 (le miroir se rafraîchira au webhook/cron).
          try { globalInvalidate?.() } catch (_) {}
          try { invalidatePricingBlocks?.() } catch (_) {}
          refreshOutbox()
          // Micro-UX M14 — « rouvrir » réussi (push OK ou mis en file, hors
          // DRY_RUN) : le miroir num_avail reste stale jusqu'au webhook/cron →
          // on marque le segment TRAITÉ (dates réelles renvoyées par le
          // serveur, fallback span cliqué) pour un rendu optimiste
          // « Réouverture envoyée ✓ » sans attendre le prochain fetch.
          // MINEUR-2 revue #993 (accepté en l'état) : le cas `queued` est
          // VOLONTAIREMENT inclus — le push part sous ≤ 15 min et laisser la
          // barre cliquable inviterait un double push ; risque atténué par le
          // toast « mise en file » + l'encart ⚠️ outbox pending.
          if (result?.mode === 'rouvrir' && !result?.dryRun) {
            const seg = {
              logementId: beds24Divergence?.logementId,
              start: result?.start || beds24Divergence?.startISO,
              end: result?.end || beds24Divergence?.endISO,
            }
            if (seg.logementId && seg.start && seg.end) {
              setBeds24ReopenedSegments((prev) => [...prev, seg])
            }
          }
          setPricingToast({
            kind: 'success',
            msg: result?.mode === 'reprendre'
              ? 'Divergence reprise dans l’app ✅'
              : result?.dryRun
                ? 'DRY_RUN : réouverture simulée ✅'
                : result?.queued
                  ? 'Réouverture mise en file (≤ 15 min) ✅'
                  : 'Disponibilité rouverte sur Beds24 ✅',
          })
          setTimeout(() => setPricingToast(null), 4000)
        }}
      />
    </div>
  )
}
